// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * The reveal pane: the tagline made literal.
 *
 * THE HARD PART IS NOT FINDING THE CHARACTERS, IT IS SHOWING THEM WITHOUT BREAKING THE TEXT.
 *
 * The obvious implementation walks the string and splices a chip element in wherever a flagged code
 * point sits. It performs, on legitimate text, exactly the damage the tool warns about:
 *
 *   · `👨‍👩‍👧` becomes `👨 ZWJ 👩 ZWJ 👧` — the reveal does the very thing the copy says
 *     stripping a joiner would do, so the user sees their emoji already in pieces and concludes the
 *     file is corrupt;
 *   · a chip inserted mid-word in Persian or Arabic breaks the cursive join, so the word visibly
 *     falls apart on ordinary, correct text;
 *   · a Devanagari conjunct or a Hangul syllable splits into its parts.
 *
 * So: SEGMENT BEFORE YOU CHIP. `Intl.Segmenter` at grapheme granularity gives the clusters a reader
 * perceives as single characters, and a cluster containing a flagged code point is rendered INTACT —
 * the real emoji, the real Persian word — carrying a marker, with the code points it contains listed
 * beside it. Nothing is ever spliced into the middle of a cluster.
 */

import { chipLabel, describeCodePoint, needsChipping } from '../core/visible';

const segmenter =
  typeof Intl !== 'undefined' && 'Segmenter' in Intl
    ? new Intl.Segmenter(undefined, { granularity: 'grapheme' })
    : null;

/** Grapheme clusters, falling back to code points where `Intl.Segmenter` is unavailable. */
export function graphemes(line: string): string[] {
  if (segmenter) return [...segmenter.segment(line)].map((s) => s.segment);
  return [...line];
}

function flaggedCodePoints(cluster: string): number[] {
  const out: number[] = [];
  for (const ch of cluster) {
    const cp = ch.codePointAt(0)!;
    if (needsChipping(cp)) out.push(cp);
  }
  return out;
}

export interface RevealOptions {
  /** Called when the user activates a cluster, with the code points it carries. */
  onSelect?: (cps: number[]) => void;
}

/**
 * Render one line into a fragment.
 *
 * A cluster that carries nothing unusual is plain text. A cluster that carries something is wrapped
 * in a `<span class="rv">` holding the cluster itself — unmodified, so it renders exactly as it
 * would anywhere else — followed by out-of-flow chips naming what is inside it. A cluster that is
 * ONLY flagged code points (a lone zero-width, a tag character) has nothing to render, so it is the
 * chip alone.
 */
export function renderLine(line: string, opts: RevealOptions = {}): DocumentFragment {
  const frag = document.createDocumentFragment();
  let plain = '';

  const flushPlain = (): void => {
    if (plain) {
      frag.append(document.createTextNode(plain));
      plain = '';
    }
  };

  for (const cluster of graphemes(line)) {
    const flagged = flaggedCodePoints(cluster);
    if (flagged.length === 0) {
      plain += cluster;
      continue;
    }
    flushPlain();

    const visible = [...cluster].filter((ch) => !needsChipping(ch.codePointAt(0)!)).join('');
    const wrap = document.createElement('span');
    wrap.className = visible ? 'rv rv-carrier' : 'rv';
    wrap.tabIndex = 0;
    wrap.setAttribute('role', 'button');
    wrap.title = flagged.map(describeCodePoint).join('\n');
    wrap.setAttribute(
      'aria-label',
      visible
        ? `${visible} — carries ${flagged.map(describeCodePoint).join(', ')}`
        : flagged.map(describeCodePoint).join(', '),
    );

    if (visible) {
      // The cluster is rendered WHOLE and unmodified. This is the point of the segmentation pass:
      // an emoji sequence stays one emoji, a Persian word keeps its joins.
      const base = document.createElement('span');
      base.className = 'rv-base';
      base.textContent = cluster;
      wrap.append(base);
    }
    for (const cp of flagged) {
      const chip = document.createElement('span');
      chip.className = 'chip-cp';
      chip.textContent = chipLabel(cp);
      wrap.append(chip);
    }

    if (opts.onSelect) {
      wrap.addEventListener('click', () => opts.onSelect!(flagged));
      wrap.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          opts.onSelect!(flagged);
        }
      });
    }
    frag.append(wrap);
  }
  flushPlain();
  return frag;
}

/** How many lines the reveal will show before it stops. A 40 MB minified file is not a reading. */
export const MAX_REVEAL_LINES = 400;

export interface RevealLine {
  number: number;
  text: string;
  interesting: boolean;
}

/**
 * Pick the lines worth showing.
 *
 * Showing the whole file is useless on anything real, and showing only the flagged lines strips
 * away the context that makes a finding legible. So: every line that carries something, plus one
 * line either side, up to a cap — and the cap is reported rather than silently applied.
 */
export function chooseLines(text: string, flaggedLines: ReadonlySet<number>): {
  lines: RevealLine[];
  truncated: number;
} {
  const all = text.split('\n');
  const wanted = new Set<number>();
  for (const n of flaggedLines) {
    for (const d of [-1, 0, 1]) {
      const i = n + d;
      if (i >= 1 && i <= all.length) wanted.add(i);
    }
  }
  if (wanted.size === 0) {
    for (let i = 1; i <= Math.min(all.length, 12); i++) wanted.add(i);
  }
  const sorted = [...wanted].sort((a, b) => a - b);
  const shown = sorted.slice(0, MAX_REVEAL_LINES);
  return {
    lines: shown.map((n) => ({
      number: n,
      text: all[n - 1] ?? '',
      interesting: flaggedLines.has(n),
    })),
    truncated: sorted.length - shown.length,
  };
}
